/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import {
  CircularProgress,
  ModalContainer,
  useDisclosure,
} from '@ouster/webviz-react-components';

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  matchPath,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import MergeRequestResponse from 'src/pages/MergeRequestResponse';
import styled from 'styled-components/macro';

import UploaderContainer from '../components/bulkUploadAndMonitor/UploaderContainer';
import ScreenRecommendation from '../components/users/ScreenRecommendation';
import ArchitectSceneList from '../containers/ArchitectSceneList';
import { ArchitectSceneViewer } from '../containers/ArchitectSceneViewer';
import { DriveViewer } from '../containers/DriveViewer';
import { FlexColumnWithNav } from '../containers/NavBar';
import useWindowResize from '../hooks/useWindowResize';
import { useKeycloak } from '../KeycloakContext';
import AdminDashboardLayoutV2 from '../layouts/AdminDashboardLayoutV2';
import AuthLayout from '../layouts/AuthLayout';
import DefaultLayout from '../layouts/DefaultLayout';
import PublicLayout from '../layouts/PublicLayout';
import SharedDriveLayout from '../layouts/SharedDriveLayout';
import AdminDashboardOrgDetail from '../pages/adminDashboard/AdminDashboardOrgDetail';
import CopiedRecordings from '../pages/adminDashboard/CopiedRecordings';
import DevToolRecordingsMgmt from '../pages/adminDashboard/DevToolRecordingsMgmt';
import InsightGrowthTrends from '../pages/adminDashboard/InsightGrowthTrends';
import OrganizationMerge from '../pages/adminDashboard/OrganizationMerge';
import OrganizationOverview from '../pages/adminDashboard/OrganizationOverview';
import OrganizationProvision from '../pages/adminDashboard/OrganizationProvision';
import RecordingDistribution from '../pages/adminDashboard/RecordingDistribution';
import RecordingLookUp from '../pages/adminDashboard/RecordingLookUp';
import SavedRecordings from '../pages/adminDashboard/SavedRecordings';
import UserLookUp from '../pages/adminDashboard/UserLookUp';
import UserPermissionLookUp from '../pages/adminDashboard/UserPermissionLookUp';
import AuthRedirect from '../pages/auth/AuthRedirect';
import LandingPage from '../pages/auth/LandingPage';
import OsdAnnouncement from '../pages/auth/OsdAnnouncement';
import PostRegistrationPage from '../pages/auth/PostRegistrationPage';
import SignUpPage from '../pages/auth/SignUpPage';
import OrganizationManagement from '../pages/OrganizationManagement';
import DriveListPage from '../pages/privateList/DriveListPage';
import PrivatePartnerPreview from '../pages/privateList/PrivatePartnerPreview';
import PrivatePublicCollection from '../pages/privateList/PrivatePublicCollection';
import PrivateRecordings from '../pages/privateList/PrivateRecordings';
import SharedWithOusterPage from '../pages/privateList/SharedWithOusterPage';
import PublicCollection from '../pages/public/PublicCollection';
import PublicPartnerPreview from '../pages/public/PublicPartnerPreview';
import PublicRecordings from '../pages/public/PublicRecordings';
import PublicSharedWithOuster from '../pages/public/PublicSharedWithOuster';
import VisualTest from '../pages/VisualTest';
import { useStoreActions, useStoreState } from '../reducers';
import { MINIMAL_WIDTH } from '../utils/constants';
import { registerSearchParamsSetter } from '../utils/searchParams';

const FullScreenFlexContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
  height: 100vh;
  height: 100dvh;
  align-items: stretch;
  background-color: var(--color-unify-darker-blue);
`;

const StatusContainer = styled.div`
  /* Keep the text to a maximum of 40ch for readibility*/
  max-width: 40ch;
  /* Center on the page */
  margin: auto;
  display: grid;
  row-gap: 3rem;
  justify-items: center;
  font-size: 1.75rem;
  font-weight: 500;
  line-height: normal;
`;

const AppLayout = () => (
  <FullScreenFlexContainer className="App">
    <Outlet />
  </FullScreenFlexContainer>
);

/* 
  After publication, authenticated doesn't mean the user can access the app
  user must existed in the db as well
  add check in private outlet
*/

const PrivateOutlet = () => {
  // Removed debugging log statement
  const { isAuthenticated, user, appUser, curUserOrg } = useStoreState(
    (storeState) => ({
      ...storeState.user,
    })
  );
  const { fetchUserRoleByIdentity, fetchUserEulaStatus, fetchUserOrgs } =
    useStoreActions((actions) => actions.user);
  const { loadNewContentStatus } = useStoreActions((actions) => actions.drive);

  const { keycloak, refreshKeycloakToken } = useKeycloak();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();

  const { fetchOrgSpaceUsage } = useStoreActions(
    (actions) => actions.organization
  );

  const { isOpen: uploadVisibility } = useStoreState(
    (storeState) => storeState.recordingUpload
  );

  const queryClient = useQueryClient();

  useEffect(() => {
    const getOrgSpaceUsage = async () => {
      if (!curUserOrg?.id) return;
      try {
        await fetchOrgSpaceUsage(curUserOrg.id);
      } catch (error) {
        console.error('fetchOrgSpaceUsage after upload', error);
      }
    };
    /* 
      We need to fetch the usage when org id change or
      when user open the upload window. so we can make sure whenever user tries to upload
      we always have its latest usage.
    */
    getOrgSpaceUsage();
  }, [curUserOrg?.id, fetchOrgSpaceUsage, uploadVisibility]);

  useEffect(() => {
    /* 
      Make sure we refetch the shared list when org change.
    */
    if (location.pathname === '/shared_with_ouster') {
      queryClient.refetchQueries({
        queryKey: ['shared-with-list', curUserOrg?.id],
        type: 'active',
      });
    } else {
      queryClient.invalidateQueries({ queryKey: ['shared-with-list'] });
    }
  }, [curUserOrg?.id, location.pathname, queryClient]);

  const [loading, setLoading] = useState(true); // Introduce a loading state
  const { width: windowWidth } = useWindowResize();

  useEffect(() => {
    if (user?.identity_id) {
      if (sessionStorage.getItem('appAuthId')) {
        navigate(
          `/app_auth?app_auth_id=${sessionStorage.getItem('appAuthId')}`
        );
        return;
      }
      if (sessionStorage.getItem('architectId')) {
        const url = new URL(window.location.origin);
        url.pathname = `/architect/${sessionStorage.getItem('architectId')}`;
        window.location.href = url.toString();

        sessionStorage.removeItem('architectId');
        return;
      }

      const fetchAppUser = async () => {
        try {
          await fetchUserRoleByIdentity(user.identity_id);
          await fetchUserOrgs(user.identity_id);

          /* 
            we don't need to await here and slow user login.
            patch user the eula status can be done in background since it won't affect anything.
          */
          try {
            fetchUserEulaStatus(user.identity_id);
          } catch (error) {
            console.error('Fetching user eula status error,', error);
          }
        } catch (error) {
          // no need to show error to user
          // it just mean the user isn't existed in db
          console.error('Fetching user by id,', error);
        } finally {
          setLoading(false);
        }
      };

      if (sessionStorage.getItem('originalPath')) {
        const originalPath = sessionStorage.getItem('originalPath') || '/';
        sessionStorage.removeItem('originalPath');

        const currentPath = `${window.location.pathname}${window.location.search}`;
        if (originalPath !== currentPath) {
          navigate(originalPath, { replace: true });
        }
      }

      fetchAppUser();
    }
  }, [
    user?.identity_id,
    fetchUserRoleByIdentity,
    fetchUserEulaStatus,
    fetchUserOrgs,
    navigate,
  ]);

  useEffect(() => {
    if (curUserOrg?.id) {
      loadNewContentStatus({ organizationId: curUserOrg.id });
    }
  }, [curUserOrg?.id, loadNewContentStatus]);

  useEffect(() => {
    // Handle navigation logic
    if (keycloak && keycloak.authenticated === false) {
      const originalPath = `${window.location.pathname}${window.location.search}`;
      keycloak.login({
        redirectUri: `${window.location.origin}${originalPath}`,
      });
      return;
    }

    if (isAuthenticated && appUser === undefined && !loading) {
      // Store the original path before redirection
      sessionStorage.setItem(
        'originalPath',
        `${window.location.pathname}${window.location.search}`
      );
      navigate('/', { replace: true });
    }
  }, [keycloak, isAuthenticated, appUser, navigate, loading]);

  useEffect(() => {
    const handleWindowFocus = () => {
      if (keycloak?.isTokenExpired()) {
        refreshKeycloakToken();
      }
    };

    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [keycloak, refreshKeycloakToken]);

  /* 
    checking screen size. when the screen is too small
    show the warning modal per session.
    User can choose to dismiss it if they don't mind and it will not show up
    again in the same session.
  */
  useEffect(() => {
    const isDriveViewerRoute =
      Boolean(
        matchPath('/organization/:orgId/drive/:driveId', location.pathname)
      ) || Boolean(matchPath('/share/:publishedDriveUuid', location.pathname));

    if (isDriveViewerRoute) {
      if (isOpen) {
        onClose();
      }
      return;
    }

    if (
      !sessionStorage.getItem('showSmallWindowWarnModal') &&
      windowWidth < MINIMAL_WIDTH
    ) {
      onOpen();
    } else if (isOpen) onClose();
  }, [isOpen, location.pathname, onClose, onOpen, windowWidth]);

  if (loading) {
    return (
      <StatusContainer>
        <span>Authenticating...</span>
        <CircularProgress isIndeterminate size="50px" thickness={10} />
      </StatusContainer>
    );
  }

  if (isAuthenticated && appUser !== undefined) {
    return (
      <>
        <UploaderContainer />
        <Outlet />
        <ModalContainer
          isOpen={isOpen}
          onClose={() => {
            sessionStorage.setItem('showSmallWindowWarnModal', 'true');
            onClose();
          }}
          header="Optimize Your Experience"
          footer={null}
          content={<ScreenRecommendation />}
        />
      </>
    );
  }

  return (
    <FlexColumnWithNav>
      <CircularProgress isIndeterminate size="50px" thickness={10} />
    </FlexColumnWithNav>
  );
};

// TODO(pierre) on 2021-12-20: The DriveList component, unlike
// DriveViewer, does not come with a Navbar. This isn't super clean and
// consistent. We should probably instead let every page handle their
// own navbar and pass the necessary info (i.e. the nav tabs and whether
// authenticated or not) through easy peasy or a React context

const ArchitectSceneListPage = () => {
  const { user } = useStoreState((storeState) => ({
    ...storeState.user,
  }));

  return <ArchitectSceneList user={user} />;
};

const RootRedirect = () => {
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);

  // Get a specific parameter value
  const specificParam = queryParams.get('path');

  let redirectRoute;
  if (specificParam) {
    redirectRoute = specificParam;
  } else {
    // The new root of the app should be the landing page.
    return <LandingPage />;
  }
  return <Navigate replace to={redirectRoute} />;
};

const KeycloakLoginRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const mode = params.get('mode');
  const returnTo = params.get('returnTo') || '/';
  const fallbackTo = params.get('fallbackTo') || returnTo;
  const probeFailed = params.get('error') === 'login_required';
  const probeAttemptKey = `kc-probe-attempt:${returnTo}`;

  const appAuthId: string | null = params.get('app_auth_id');
  if (appAuthId) {
    sessionStorage.setItem('appAuthId', appAuthId);
  }
  const architectId: string | null = params.get('architect');
  if (architectId) {
    sessionStorage.setItem('architectId', architectId);
  }
  const appAuthProtocol: string | null = params.get('app_auth_protocol');
  if (appAuthProtocol) {
    sessionStorage.setItem('appAuthProtocol', appAuthProtocol);
  }
  const organizationId = params.get('organization_id');
  if (organizationId) {
    localStorage.setItem('lastOrgId', organizationId);
  }

  const { keycloak } = useKeycloak();

  useEffect(() => {
    if (mode === 'probe' && probeFailed) {
      sessionStorage.removeItem(probeAttemptKey);
      navigate(fallbackTo, { replace: true });
      return;
    }

    if (!keycloak) {
      return;
    }

    if (mode === 'probe' && keycloak.authenticated) {
      sessionStorage.removeItem(probeAttemptKey);
      return;
    }

    if (!keycloak.authenticated) {
      if (mode === 'probe') {
        const probeAttempt = sessionStorage.getItem(probeAttemptKey);

        if (probeAttempt === 'started') {
          sessionStorage.removeItem(probeAttemptKey);
          navigate(fallbackTo, { replace: true });
          return;
        }

        sessionStorage.setItem(probeAttemptKey, 'started');
        keycloak.login({
          prompt: 'none',
          redirectUri: `${
            window.location.origin
          }/login?mode=probe&returnTo=${encodeURIComponent(
            returnTo
          )}&fallbackTo=${encodeURIComponent(fallbackTo)}`,
        });
        return;
      }

      keycloak.login({
        redirectUri: `${window.location.origin}`,
      });
    }
  }, [
    fallbackTo,
    keycloak,
    mode,
    navigate,
    probeAttemptKey,
    probeFailed,
    returnTo,
  ]);

  // this is first time introducing osd version check. Hence, we will
  // only do the check when osd_version is provided. in future, we will might do detailed
  // version check and provide version specific message.
  // make sure we only do this for osd auth request, so osw won't be affected.
  // comment out the osd version check since we will still support two keycloak domain in this release.
  // TODO: (vivian) remove the comment below when we decide to enforce osd version check
  // const osdVersion: string | null = params.get('osd_version');
  // if (!osdVersion && appAuthId) {
  //   return <Navigate to="/osd_announcement" replace />;
  // }
  // if (osdVersion) sessionStorage.setItem('osdVersion', osdVersion);

  if (mode === 'probe' && probeFailed) {
    return null;
  }

  if (keycloak?.authenticated) {
    return <Navigate to={mode === 'probe' ? returnTo : '/'} replace />;
  }

  return null;
};

/* 
  To prevent user accidentally remove the orgId from the url, we moved the private drive router from drive/:driverId?orgId=orgId to organization/:orgId/drive/:driveId
  This component will redirect the old route to the new one.

  when the orgId is not provided in the url, we will use the current user org id, this is the same behavior as we handle in the DriveViewer component.
*/
const RedirectOldDriveRoute = () => {
  const { curUserOrg } = useStoreState((storeState) => ({
    ...storeState.user,
  }));

  const [searchParams] = useSearchParams();
  const { driveId } = useParams(); // Extract driveId from the route params
  const navigate = useNavigate();

  useEffect(() => {
    const orgId = searchParams.get('orgId') ?? curUserOrg?.id.toString();
    const frame = searchParams.get('frame');

    if (orgId && driveId) {
      const newSearchParams = new URLSearchParams();
      newSearchParams.set('orgId', orgId);
      if (frame) {
        newSearchParams.set('frame', frame);
      }
      navigate(
        `/organization/${orgId}/drive/${driveId}?${newSearchParams.toString()}`,
        { replace: true }
      );
    }
  }, [searchParams, driveId, navigate, curUserOrg?.id]);

  return null;
};

export default function AppRoutes() {
  const [, setSearchParams] = useSearchParams();
  registerSearchParamsSetter(setSearchParams);

  return (
    <Routes>
      <Route path="/" element={<AppLayout />}>
        <Route element={<AuthLayout />}>
          <Route index element={<RootRedirect />} />
          <Route path="sign_up" element={<SignUpPage />} />
          <Route path="login" element={<KeycloakLoginRedirect />} />
          <Route path="post-registration" element={<PostRegistrationPage />} />
        </Route>

        <Route path="app_auth" element={<AuthRedirect />} />
        <Route path="osd_announcement" element={<OsdAnnouncement />} />

        {/* public in-app pages for non-logged user to experience */}
        <Route path="/public" element={<PublicLayout />}>
          {/* If user visit the /public directly, redirect user to the sample page */}
          <Route
            index
            element={<Navigate to="sample_library/public_collection" replace />}
          />
          <Route path="recordings" element={<PublicRecordings />} />
          <Route
            path="shared_with_ouster"
            element={<PublicSharedWithOuster />}
          />
          {/* Sample Library routes */}
          <Route path="sample_library">
            {/* Index route redirects to first submenu */}
            <Route
              index
              element={<Navigate to="public_collection" replace />}
            />
            <Route path="public_collection" element={<PublicCollection />} />
            <Route path="partner_preview" element={<PublicPartnerPreview />} />
          </Route>
        </Route>

        <Route element={<PrivateOutlet />}>
          <Route
            path="architect_scene/:architectSceneId"
            element={<ArchitectSceneViewer />}
          />
          <Route path="drive/:driveId" element={<RedirectOldDriveRoute />} />
          <Route
            path="organization/:orgId/drive/:driveId"
            element={<DriveViewer />}
          />

          <Route path="admin_dashboard" element={<AdminDashboardLayoutV2 />}>
            <Route
              index
              element={<Navigate to="insights/growth_trends" replace />}
            />
            {/* Insights */}
            <Route
              path="insights/growth_trends"
              element={<InsightGrowthTrends />}
            />
            {/* User Management */}
            <Route
              path="user_management/user_search"
              element={<UserLookUp />}
            />
            <Route
              path="user_management/role_permissions"
              element={<UserPermissionLookUp />}
            />
            {/* Organization Hub */}
            <Route
              path="organization_hub/org_overview"
              element={<OrganizationOverview />}
            />
            <Route
              path="organization_hub/org_overview/:orgId"
              element={<AdminDashboardOrgDetail />}
            />
            <Route
              path="organization_hub/provisioning"
              element={<OrganizationProvision />}
            />
            <Route
              path="organization_hub/merge"
              element={<OrganizationMerge />}
            />
            {/* Recording Controls */}
            <Route
              path="recording_controls/recording_lookup"
              element={<RecordingLookUp />}
            />
            <Route
              path="recording_controls/copied_recordings"
              element={<CopiedRecordings />}
            />
            <Route
              path="recording_controls/saved_recordings"
              element={<SavedRecordings />}
            />
            <Route
              path="recording_controls/distribution"
              element={<RecordingDistribution />}
            />
            {/* Developer Tools */}
            <Route
              path="developer_tools/recordings_mgmt"
              element={<DevToolRecordingsMgmt />}
            />
          </Route>

          {/* Routes with DefaultLayout */}
          <Route element={<DefaultLayout />}>
            <Route
              index
              path="architect_scene"
              element={<ArchitectSceneListPage />}
            />
            <Route path="drive/">
              <Route index element={<DriveListPage />} />
              <Route path="private" element={<PrivateRecordings />} />

              <Route path="sample_library">
                {/* When user clicks sample_library, redirect to public_collection */}
                <Route
                  index
                  element={<Navigate to="public_collection" replace />}
                />
                <Route
                  path="public_collection"
                  element={<PrivatePublicCollection />}
                />
                <Route
                  path="partner_preview"
                  element={<PrivatePartnerPreview />}
                />
              </Route>
            </Route>

            <Route
              index
              path="organization"
              element={<OrganizationManagement />}
            />

            <Route
              index
              path="shared_with_ouster"
              element={<SharedWithOusterPage />}
            />
          </Route>

          <Route path="merge_response" element={<MergeRequestResponse />} />
        </Route>

        <Route path="share" element={<SharedDriveLayout />}>
          <Route path=":publishedDriveUuid" element={<DriveViewer />} />
        </Route>

        <Route path="architect/share">
          <Route
            path=":publishedArchitectSceneUuid"
            element={<ArchitectSceneViewer />}
          />
        </Route>

        <Route path="test_visual" element={<VisualTest />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
